import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { useAuthStore } from '@/stores/authStore';
import { LanguageToggle } from '@/components/LanguageToggle';
import { LogOut, Users, Calendar, AlertTriangle, Settings, Search, ChevronLeft, ChevronRight, Pencil, Trash2, Eye, Plus, ClipboardList } from 'lucide-react';

export function DoctorDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Patient catalog state
  const [patients, setPatients] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const patientsPerPage = 5;
  
  // Add patient modal state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState<any>(null);
  const [form, setForm] = useState({
    parent_email: '',
    first_name: '',
    last_name: '',
    dob: '',
    sex: 'not_specified' as 'male' | 'female' | 'not_specified',
    vaccines_up_to_date: false,
  });

  useEffect(() => {
    // Check if we got a token from Cloudflare Access redirect
    const token = searchParams.get('token');
    if (token) {
      // Store the token and authenticate
      localStorage.setItem('auth_token', token);
      localStorage.setItem('userType', 'doctor');
      localStorage.setItem('isAuthenticated', 'true');
      
      useAuthStore.setState({
        isAuthenticated: true,
        userType: 'doctor',
      });

      // Remove token from URL
      setSearchParams({});
      
      // Load dashboard
      loadDashboard();
      return;
    }

    // Check if already authenticated (check localStorage directly for reload case)
    const storedToken = localStorage.getItem('auth_token');
    const storedUserType = localStorage.getItem('userType');
    
    if (!storedToken || storedUserType !== 'doctor') {
      navigate('/doctor/login');
      return;
    }

    // Sync Zustand state if needed
    if (!isAuthenticated) {
      useAuthStore.setState({
        isAuthenticated: true,
        userType: 'doctor',
      });
    }

    loadDashboard();
  }, [searchParams, navigate, setSearchParams]);

  const loadDashboard = async () => {
    try {
      const result: any = await api.getDashboard();
      setStats(result.stats);
      
      // Load patients for catalog
      const patientsResult: any = await api.getPatients();
      setPatients(patientsResult.results || patientsResult.patients || []);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/logout?type=doctor');
  };

  const handleEditPatient = (patient: any) => {
    const nameParts = patient.name.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setEditingPatient(patient);
    setForm({
      parent_email: patient.parent_email || '',
      first_name: firstName,
      last_name: lastName,
      dob: patient.dob || '',
      sex: patient.sex || 'not_specified',
      vaccines_up_to_date: patient.vaccines_up_to_date || false,
    });
    setShowEditForm(true);
  };

  const handleDeletePatient = async (patient: any) => {
    if (!confirm(`${t('common.confirmDelete')} ${patient.name}?`)) return;
    try {
      await api.deletePatient(patient.id);
      toast({ title: t('patient.deleted') });
      loadDashboard();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
    }
  };

  const handleViewPatient = (patientId: string) => {
    navigate(`/doctor/patients/${patientId}`);
  };
  
  const openAddPatient = () => {
    setForm({ parent_email: '', first_name: '', last_name: '', dob: '', sex: 'not_specified', vaccines_up_to_date: false });
    setShowAddForm(true);
  };
  
  const saveNewPatient = async () => {
    try {
      const payload = {
        parent_email: form.parent_email,
        name: `${form.first_name} ${form.last_name}`.trim(),
        dob: form.dob,
        sex: form.sex,
        vaccines_up_to_date: form.vaccines_up_to_date,
      };
      await api.createPatient(payload);
      toast({ title: t('patient.created') });
      
      // Reload patients
      const result: any = await api.getPatients();
      setPatients(result.results || result.patients || []);
      setShowAddForm(false);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
    }
  };
  
  const saveEditPatient = async () => {
    if (!editingPatient) return;
    try {
      const payload = {
        parent_email: form.parent_email,
        name: `${form.first_name} ${form.last_name}`.trim(),
        dob: form.dob,
        sex: form.sex,
        vaccines_up_to_date: form.vaccines_up_to_date,
      };
      await api.updatePatient(editingPatient.id, payload);
      toast({ title: t('patient.updated') });
      
      // Reload patients
      const result: any = await api.getPatients();
      setPatients(result.results || result.patients || []);
      setShowEditForm(false);
      setEditingPatient(null);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
    }
  };

  // Filter and paginate patients
  const filteredPatients = patients.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);
  const startIndex = (currentPage - 1) * patientsPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, startIndex + patientsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <img src="https://pub-327c49603f8c4373ac8b69237691fafc.r2.dev/rds-logo.png" alt="Logo" className="h-10 w-10 sm:h-12 sm:w-12" />
            {t('nav.dashboard')}
          </h1>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => navigate('/doctor/vaccines')} className="touch-action-manipulation min-h-[44px]">
              {t('nav.vaccines')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/doctor/settings')} className="touch-action-manipulation min-h-[44px]">
              <Settings className="h-4 w-4" />
            </Button>
            <LanguageToggle />
            <Button variant="outline" onClick={handleLogout} className="touch-action-manipulation min-h-[44px]">
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.totalPatients')}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_patients || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.upcomingDoses')}</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.upcoming_doses || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.overdueDoses')}</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats?.overdue_doses || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Patient Catalog Card */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('nav.patients')}
              </CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('common.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Button size="sm" onClick={openAddPatient} className="touch-action-manipulation">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('patient.add')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {paginatedPatients.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                {searchQuery ? t('patient.noResults') : t('patient.emptyDescription')}
              </p>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3">
                  {paginatedPatients.map((patient) => (
                    <div
                      key={patient.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold truncate">{patient.name}</h4>
                        <p className="text-sm text-gray-500">
                          {t('patient.dob')}: {new Date(patient.dob).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewPatient(patient.id)}
                          title={t('common.view')}
                          className="touch-action-manipulation min-h-[44px] min-w-[44px]"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/doctor/patients/${patient.id}?action=logVisit`)}
                          title={t('visit.logVisit')}
                          className="touch-action-manipulation min-h-[44px] min-w-[44px]"
                        >
                          <ClipboardList className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditPatient(patient)}
                          title={t('common.edit')}
                          className="touch-action-manipulation min-h-[44px] min-w-[44px]"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeletePatient(patient)}
                          title={t('common.delete')}
                          className="touch-action-manipulation min-h-[44px] min-w-[44px]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t gap-3">
                    <p className="text-sm text-gray-500">
                      {t('common.showing')} {startIndex + 1}-{Math.min(startIndex + patientsPerPage, filteredPatients.length)} {t('common.of')} {filteredPatients.length}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="touch-action-manipulation min-h-[44px] min-w-[44px]"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="flex items-center px-3 text-sm">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* Add Patient Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">{t('patient.add')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first">{t('patient.firstName')}</Label>
                <Input id="first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="last">{t('patient.lastName')}</Label>
                <Input id="last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="dob">{t('patient.dob')}</Label>
                <Input id="dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="sex">{t('patient.sex')}</Label>
                <Select value={form.sex} onValueChange={(value: 'male' | 'female' | 'not_specified') => setForm({ ...form, sex: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('patient.sexNotSpecified')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('patient.sexMale')}</SelectItem>
                    <SelectItem value="female">{t('patient.sexFemale')}</SelectItem>
                    <SelectItem value="not_specified">{t('patient.sexNotSpecified')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.vaccines_up_to_date} onChange={(e) => setForm({ ...form, vaccines_up_to_date: e.target.checked })} />
                  <span>{t('patient.vaccinesUpToDate')}</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>{t('common.cancel')}</Button>
              <Button onClick={saveNewPatient}>{t('common.save')}</Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Patient Modal */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">{t('patient.edit')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-first">{t('patient.firstName')}</Label>
                <Input id="edit-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-last">{t('patient.lastName')}</Label>
                <Input id="edit-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-email">{t('auth.email')}</Label>
                <Input id="edit-email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-dob">{t('patient.dob')}</Label>
                <Input id="edit-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-sex">{t('patient.sex')}</Label>
                <Select value={form.sex} onValueChange={(value: 'male' | 'female' | 'not_specified') => setForm({ ...form, sex: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('patient.sexNotSpecified')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('patient.sexMale')}</SelectItem>
                    <SelectItem value="female">{t('patient.sexFemale')}</SelectItem>
                    <SelectItem value="not_specified">{t('patient.sexNotSpecified')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.vaccines_up_to_date} onChange={(e) => setForm({ ...form, vaccines_up_to_date: e.target.checked })} />
                  <span>{t('patient.vaccinesUpToDate')}</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowEditForm(false); setEditingPatient(null); }}>{t('common.cancel')}</Button>
              <Button onClick={saveEditPatient}>{t('common.save')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
